import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SchoolIcon from '@mui/icons-material/School';
import axios from '../../utils/axios';

const ExamResult = () => {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    useEffect(() => {
        const fetchResult = async () => {
            try {
                let resultData;
                if (location.state?.result) {
                    // If result is passed through navigation state (just completed exam)
                    resultData = location.state.result;
                } else if (id) {
                    // If viewing a past result
                    const response = await axios.get(`/api/exams/results/${id}`);
                    resultData = response.data;
                } else {
                    throw new Error('No result data available');
                }
                setResult(resultData);
                setLoading(false);
            } catch (err) {
                setError('Failed to load exam result');
                setLoading(false);
            }
        };

        fetchResult();
    }, [id, location.state]);

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !result) {
        return (
            <Container maxWidth="md">
                <Alert severity="error">{error || 'No result found'}</Alert>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/exams')}
                    sx={{ mt: 2 }}
                >
                    Back to Exams
                </Button>
            </Container>
        );
    }

    const score = Math.round((result.correctAnswers / result.totalQuestions) * 100);
    const questions = result.questions || [];

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Exam Results
                </Typography>
                <Typography variant="h5" component="h2" gutterBottom>
                    {result.exam?.title || 'Exam'}
                </Typography>
                <Typography variant="h3" component="div" color="primary" gutterBottom>
                    {score}%
                </Typography>
                <Typography variant="body1" gutterBottom>
                    You answered {result.correctAnswers} out of {result.totalQuestions} questions correctly.
                </Typography>
                {result.duration > 0 ? (
                    <Typography variant="body1" color="text.secondary">
                        Time taken: {formatTime(result.duration)}
                    </Typography>
                ) : location.state?.timeElapsed ? (
                    <Typography variant="body1" color="text.secondary">
                        Time taken: {location.state.timeElapsed}
                    </Typography>
                ) : null}
            </Paper>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    Question Review
                </Typography>
                {questions.map((q, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Question {index + 1}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {q.question}
                        </Typography>
                        <Typography
                            variant="body1"
                            color={q.isCorrect ? 'success.main' : 'error.main'}
                            gutterBottom
                        >
                            Your answer: {q.userAnswer}
                        </Typography>
                        {!q.isCorrect && (
                            <Typography variant="body1" color="success.main" gutterBottom>
                                Correct answer: {q.correctAnswer}
                            </Typography>
                        )}
                        {q.explanation && (
                            <Typography variant="body2" color="text.secondary">
                                Explanation: {q.explanation}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Paper>

            <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/exams/history')}
                >
                    View All Results
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/exams')}
                >
                    Take Another Exam
                </Button>
            </Box>
        </Container>
    );
};

export default ExamResult; 